import { RouteRecordRaw } from 'vue-router';
import { EmployeeRouteName } from '../constants/routesName';
import { UserRole, ROUTE_CATEGORY } from '../types';

const AroundTheWorldManagement = () =>
  import('@/features/around-the-world/views/AroundTheWorldManagement.vue');
const AroundTheWorldManagementMap = () =>
  import('@/features/around-the-world/views/AroundTheWorldMap.vue');
const DashboardCampaignManagement = () =>
  import('@/features/my-study-abroad/collaborators/views/CampaignsManagementPage.vue');
const CampaignDashboard = () =>
  import('@/features/my-study-abroad/collaborators/views/campaign-information/CampaignDashboard.vue');
const CampaignForm = () =>
  import('@/features/my-study-abroad/collaborators/views/campaign-information/CampaignForm.vue');
const StudentDashboard = () =>
  import('@/features/my-study-abroad/collaborators/views/student-information/StudentDashboard.vue');
const StudentForm = () =>
  import('@/features/my-study-abroad/collaborators/views/student-information/StudentForm.vue');
const UniversitiesDashboard = () =>
  import('@/features/my-study-abroad/collaborators/views/destination-information/UniversitiesDashboard.vue');
const UniversityAgreementDashboard = () =>
  import('@/features/my-study-abroad/collaborators/views/destination-information/UniversityAgreementDashboard.vue');
const DestinationForm = () =>
  import('@/features/my-study-abroad/collaborators/views/destination-information/DestinationForm.vue');
const CampaignParametragePage = () =>
  import('@/features/my-study-abroad/collaborators/views/parametrage/views/CampaignParametragePage.vue');
const StudentViewerPage = () => import('@/features/student-viewer/views/StudentViewerPage.vue');

const DisponibilitiesPage = () => import('@/features/rooms/views/DisponibilitiesPage.vue');
const EmployeeHomePage = () => import('@/features/homepage/views/EmployeeHomePage.vue');
const MenuManagement = () => import('@/features/menu-management/views/MenuManagementPage.vue');
const PlanningPage = () => import('@/features/rooms/views/PlanningPage.vue');
const ExpatriationEligibilityPage = () =>
  import('@/features/expatriation-eligibility/views/ExpatriationEligibilityPage.vue');

/**
 * Routes dédiées aux collaborateurs
 */
const campaignWorkspaceMeta = {
  requiresRoles: [UserRole.ADMINISTRATOR, UserRole.DirectionAffairesInternationales],
  title: 'routes.campaignManagement',
  icon: 'time-outline',
  category: ROUTE_CATEGORY.COLLABORATEUR,
  hideInMenu: true,
} as const;

export const employeeRoutes: RouteRecordRaw[] = [
  {
    path: '/employee-home',
    name: EmployeeRouteName.EmployeeHome,
    component: EmployeeHomePage,
    meta: {
      requiresRoles: [UserRole.COLLABORATEUR],
      title: 'routes.employeeHome',
      icon: 'home-outline',
      category: ROUTE_CATEGORY.COLLABORATEUR,
    },
  },
  // Rooms routes
  {
    path: '/rooms/planning',
    name: EmployeeRouteName.RoomsPlanning,
    component: PlanningPage,
    meta: {
      requiresRoles: [UserRole.COLLABORATEUR],
      title: 'routes.roomsPlanning',
      icon: 'business-outline',
      category: ROUTE_CATEGORY.COLLABORATEUR,
    },
  },
  {
    path: '/rooms/disponibilities',
    name: EmployeeRouteName.RoomsDisponibilities,
    component: DisponibilitiesPage,
    meta: {
      requiresRoles: [UserRole.COLLABORATEUR],
      title: 'routes.roomsAvailability',
      icon: 'calendar-outline',
      category: ROUTE_CATEGORY.COLLABORATEUR,
    },
  },
  // Around The World routes
  {
    path: '/around-the-world-management',
    name: EmployeeRouteName.AroundTheWorldManagement,
    component: AroundTheWorldManagement,
    meta: {
      requiresRoles: [UserRole.COLLABORATEUR],
      title: 'routes.aroundTheWorld',
      icon: 'earth-outline',
      category: ROUTE_CATEGORY.COLLABORATEUR,
    },
  },
  {
    path: '/around-the-world-management/map',
    name: EmployeeRouteName.AroundTheWorldManagementMap,
    component: AroundTheWorldManagementMap,
    meta: {
      requiresRoles: [UserRole.COLLABORATEUR],
      title: 'routes.aroundTheWorld',
      icon: 'earth-outline',
      hideInMenu: true,
      category: ROUTE_CATEGORY.COLLABORATEUR,
    },
  },
  {
    path: '/menu-management',
    name: EmployeeRouteName.MenuManagement,
    component: MenuManagement,
    meta: {
      requiresRoles: [UserRole.ADMINISTRATOR],
      title: 'routes.menuManagement',
      icon: 'link-outline',
      category: ROUTE_CATEGORY.COLLABORATEUR,
    },
  },
  {
    path: '/campaign-management',
    name: EmployeeRouteName.CampaignManagement,
    component: DashboardCampaignManagement,
    meta: {
      requiresRoles: [UserRole.ADMINISTRATOR, UserRole.DirectionAffairesInternationales],
      title: 'routes.campaignManagement',
      icon: 'time-outline',
      category: ROUTE_CATEGORY.COLLABORATEUR,
      hideInMenu: false,
    },
  },
  // Création d'une campagne : formulaire autonome (pas d'id de campagne existant).
  {
    path: '/campaign-management/new',
    name: EmployeeRouteName.CampaignManagementCreate,
    component: CampaignForm,
    props: { id: NaN, isDisabled: false },
    meta: {
      requiresRoles: [UserRole.ADMINISTRATOR, UserRole.DirectionAffairesInternationales],
      title: 'routes.campaignManagement',
      icon: 'time-outline',
      category: ROUTE_CATEGORY.COLLABORATEUR,
      hideInMenu: true,
    },
  },
  // Routes statiques avant la route dynamique ":id" pour éviter qu'elles soient capturées.
  {
    path: '/campaign-management/parametrage',
    name: EmployeeRouteName.CampaignParametrage,
    component: CampaignParametragePage,
    meta: {
      requiresRoles: [UserRole.ADMINISTRATOR, UserRole.DirectionAffairesInternationales],
      title: 'routes.campaignParametrage',
      icon: 'build-outline',
      category: ROUTE_CATEGORY.COLLABORATEUR,
      hideInMenu: true,
    },
  },
  // Espace de travail d'une campagne : routes à plat (compatibles avec ion-router-outlet).
  // Chaque route résout son composant et ses props depuis l'URL ; le fil d'Ariane est
  // reconstruit via `meta.parent` / `meta.breadcrumb`.
  {
    path: '/campaign-management/:id',
    name: EmployeeRouteName.CampaignManagementDetails,
    component: CampaignDashboard,
    props: (route) => ({ campaignId: Number(route.params.id) }),
    meta: {
      ...campaignWorkspaceMeta,
      breadcrumb: 'study-abroad.breadcrumbs.dashboard',
    },
  },
  {
    path: '/campaign-management/:id/informations',
    name: EmployeeRouteName.CampaignManagementInfo,
    component: CampaignForm,
    props: (route) => ({ id: Number(route.params.id), isDisabled: false }),
    meta: {
      ...campaignWorkspaceMeta,
      breadcrumb: 'study-abroad.breadcrumbs.campaign-info',
      parent: EmployeeRouteName.CampaignManagementDetails,
    },
  },
  {
    path: '/campaign-management/:id/students',
    name: EmployeeRouteName.CampaignManagementStudents,
    component: StudentDashboard,
    props: (route) => ({ campaignId: Number(route.params.id) }),
    meta: {
      ...campaignWorkspaceMeta,
      breadcrumb: 'study-abroad.breadcrumbs.student-dashboard',
      parent: EmployeeRouteName.CampaignManagementDetails,
    },
  },
  {
    path: '/campaign-management/:id/students/:studentId',
    name: EmployeeRouteName.CampaignManagementStudentInfo,
    component: StudentForm,
    props: (route) => ({
      campaignId: Number(route.params.id),
      studentId: Number(route.params.studentId),
    }),
    meta: {
      ...campaignWorkspaceMeta,
      breadcrumb: 'study-abroad.breadcrumbs.student-info',
      parent: EmployeeRouteName.CampaignManagementStudents,
    },
  },
  {
    path: '/campaign-management/:id/universities',
    name: EmployeeRouteName.CampaignManagementUniversities,
    component: UniversitiesDashboard,
    props: (route) => ({ campaignId: Number(route.params.id) }),
    meta: {
      ...campaignWorkspaceMeta,
      breadcrumb: 'study-abroad.breadcrumbs.universities-dashboard',
      parent: EmployeeRouteName.CampaignManagementDetails,
    },
  },
  {
    path: '/campaign-management/:id/universities/:universityId/agreements',
    name: EmployeeRouteName.CampaignManagementUniversityAgreements,
    component: UniversityAgreementDashboard,
    props: (route) => ({
      campaignId: Number(route.params.id),
      universityId: Number(route.params.universityId),
      universityName: route.query.universityName as string | undefined,
    }),
    meta: {
      ...campaignWorkspaceMeta,
      breadcrumb: 'study-abroad.breadcrumbs.university-agreement-dashboard',
      parent: EmployeeRouteName.CampaignManagementUniversities,
    },
  },
  {
    path: '/campaign-management/:id/universities/:universityId/destination/:agreementId?',
    name: EmployeeRouteName.CampaignManagementDestinationInfo,
    component: DestinationForm,
    props: (route) => ({
      campaignId: Number(route.params.id),
      universityId: Number(route.params.universityId),
      agreementId: route.params.agreementId ? Number(route.params.agreementId) : undefined,
      universityName: route.query.universityName as string | undefined,
      duplicate: route.query.duplicate === 'true',
    }),
    meta: {
      ...campaignWorkspaceMeta,
      breadcrumb: 'study-abroad.breadcrumbs.destination-info',
      parent: EmployeeRouteName.CampaignManagementUniversityAgreements,
    },
  },
  {
    path: '/campaign-management/debug-board',
    name: EmployeeRouteName.CampaignDebugBoard,
    component: () =>
      import('@/features/my-study-abroad/collaborators/views/CampaignDebugBoard.vue'),
    meta: {
      requiresRoles: [UserRole.ADMINISTRATOR],
      title: 'routes.campaignDebugBoard',
      icon: 'bug-outline',
      category: ROUTE_CATEGORY.COLLABORATEUR,
      hideInMenu: true,
    },
  },
  {
    path: '/student-viewer',
    name: EmployeeRouteName.StudentViewer,
    component: StudentViewerPage,
    meta: {
      requiresRoles: [UserRole.COLLABORATEUR, UserRole.ADMINISTRATOR],
      title: 'routes.studentViewer',
      icon: 'people-outline',
      category: ROUTE_CATEGORY.COLLABORATEUR,
    },
  },
  {
    path: '/expatriation-eligibility',
    name: EmployeeRouteName.ExpatriationEligibility,
    component: ExpatriationEligibilityPage,
    meta: {
      requiresRoles: [
        UserRole.COLLABORATEUR,
        UserRole.DirectionAffairesInternationales,
        UserRole.ADMINISTRATOR,
      ],
      title: 'routes.expatriationEligibility',
      icon: 'globe-outline',
      category: ROUTE_CATEGORY.COLLABORATEUR,
    },
  },
];
